import React from "react";
import {
  Modal,
  View,
  Text,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
} from "react-native";
import {
  COLORS,
  COMMON_STYLES,
  LazyImage,
  SUBHEADING_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from "@dehaat/dds";
import BackCaret from "./icons/BackCaret";
import { HourlyTemperature, WeeklyTemperature } from "../models/advistory";

interface ForecastModalProps {
  type: string;
  visible: boolean;
  title: string;
  subTitle?: string;
  data: HourlyTemperature[] | WeeklyTemperature[] | [];
  alert?: string;
  onClose: () => void;
  alertIcon: string;
  rainIcon: string;
}

const ForecastModal = ({
  type,
  visible,
  title,
  subTitle,
  data,
  alert,
  onClose,
  alertIcon,
  rainIcon,
}: ForecastModalProps) => {
  return (
    <Modal visible={visible} animationType="slide">
      <View style={styles.container}>
        <View
          style={[COMMON_STYLES.row, COMMON_STYLES.p1, COMMON_STYLES.divider]}
        >
          <TouchableOpacity onPress={onClose}>
            <BackCaret height={25} width={25} />
          </TouchableOpacity>
          <Text
            style={[
              SUBHEADING_STYLES.MEDIUM_S3,
              { color: COLORS.neutral90 },
              COMMON_STYLES["ml0.5"],
            ]}
          >
            {title}
          </Text>
        </View>

        <View style={[COMMON_STYLES.flex1, COMMON_STYLES.alignItemsCenter]}>
          <View style={[COMMON_STYLES.flex1, { width: "100%", maxWidth: 600 }]}>
            {subTitle && (
              <Text
                style={[
                  styles.sub,
                  COMMON_STYLES["p0.5"],
                  COMMON_STYLES.textCenter,
                  VARIANTS_STYLES.SEMI_EMPHASIZED,
                ]}
              >
                {subTitle}
              </Text>
            )}

            <ScrollView
              style={[
                COMMON_STYLES.flex1,
                { overflow: "scroll" },
                COMMON_STYLES.p1,
              ]}
            >
              {data?.map((item, index) => {
                const temp =
                  type === "hourly"
                    ? `${(item as HourlyTemperature).temp}°C`
                    : `${(item as WeeklyTemperature).max}°C | ${
                        (item as WeeklyTemperature).min
                      }°C`;
                return (
                  <View key={index} style={[styles.row]}>
                    <View
                      style={[
                        COMMON_STYLES.row,
                        COMMON_STYLES.alignItemsCenter,
                        { flex: 0.4 },
                      ]}
                    >
                      <Text style={[styles.label, COMMON_STYLES["mr0.5"]]}>
                        {(item as HourlyTemperature).time ||
                          (item as WeeklyTemperature).day}
                      </Text>
                      <LazyImage
                        imageUrl={item.icon}
                        imgStyles={styles.weatherIcon}
                      />
                    </View>
                    {rainIcon ? (
                      <View style={[COMMON_STYLES.row]}>
                        <LazyImage
                          imageUrl={rainIcon}
                          imgStyles={styles.weatherIcon}
                        />
                        <Text style={[TYPOGRAPHY.CAPTION1]}>
                          {item.rain_percent || 0}%
                        </Text>
                      </View>
                    ) : null}

                    <Text style={styles.temp}>{temp}</Text>
                  </View>
                );
              })}

              {alert && (
                <View style={styles.noteBox}>
                  <LazyImage
                    imageUrl={alertIcon}
                    imgStyles={styles.weatherIcon}
                  />
                  <Text style={[COMMON_STYLES["ml0.5"]]}>{alert}</Text>
                </View>
              )}
            </ScrollView>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.white,
  },
  sub: {
    color: COLORS.neutral90,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 12,
    alignItems: "center",
  },
  label: {
    fontSize: 14,
    color: COLORS.neutral100,
    flex: 1,
  },
  icon: {
    fontSize: 18,
    marginHorizontal: 8,
  },
  temp: {
    fontSize: 14,
    color: COLORS.neutral100,
  },
  noteBox: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: COLORS.neutral10,
    padding: 12,
    marginVertical: 12,
    borderRadius: 8,
  },
  weatherIcon: {
    width: 20,
    height: 20,
    resizeMode: "contain",
  },
});

export default ForecastModal;
